import React, {SyntheticEvent, useEffect, useState} from "react";
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import useCountDown from "react-countdown-hook";
import {Layout} from '../components/common/Layout';
import {useLazyQuery} from "@apollo/client";
import {REQUEST_OTP} from "../api/auth/requestOtp";
import {ErrorMessage} from "../components/common/ErrorMessage";
import {LOGIN} from "../api/auth/login";
import {EMAIL_REGEX, OTP_REGEX} from "../constants/regex";
import { useNavigate } from "react-router-dom";
import Typography from '@mui/material/Typography';

type Step = 'email' | 'otp'

export const Login = () => {
    const [email, setEmail] = useState('')
    const [otp, setOtp] = useState('')
    const [isEmailValid, setIsEmailValid] = useState(false)
    const [isOtpValid, setIsOtpValid] = useState(false)
    const [step, setStep] = useState<Step>('email')
    const [snackBarMessage, setSnackBarMessage] = useState('')

    const navigate = useNavigate();

    const [requestOtp, {loading: requestOtpLoading, error: otpError}] = useLazyQuery(REQUEST_OTP);

    const [login, {loading: loginLoading, error: loginError}] = useLazyQuery(LOGIN);

    const [timeLeft, actions] = useCountDown(60000, 1000);

    const onStepChange = (nextStep: Step) => {
        setStep(nextStep)

        if (nextStep === 'otp') {
            actions.start()
        }
    }

    const onEmailChange = (nextValue: string) => {
        if (EMAIL_REGEX.test(nextValue)) {
            setIsEmailValid(true)
        } else {
            setIsEmailValid(false)
        }

        setEmail(nextValue)
    }

    const onRequestOtp = async (event?: SyntheticEvent) => {
        if (event) {
            event.preventDefault()
        }

        const {error, data} = await requestOtp({variables: {email}})

        if (error === undefined && data !== undefined && data.requestOtp) {
            onStepChange('otp')
            setSnackBarMessage('')
        } else if (error === undefined && data !== undefined && !data.requestOtp) {
            setSnackBarMessage('The provided email isn\'t valid')
        }
    }

    const onOtpChange = (nextValue: string) => {
        if (OTP_REGEX.test(nextValue)) {
            setIsOtpValid(true)
        } else {
            setIsOtpValid(false)
        }

        setOtp(nextValue)
    }

    const onResend = async () => {
        await onRequestOtp()
        actions.start()
    }

    const onLogin = async (event: SyntheticEvent) => {
        event.preventDefault()
        const {data, error} = await login({variables: {email, otp}})

        if (error === undefined && data !== undefined) {
            localStorage.setItem("auth", JSON.stringify(data.login))

            if (data.login.user.role === 'CLIENT') navigate("/my-application");
            else navigate("/leads");
        }
    }

    useEffect(() => {
        if (loginError) setSnackBarMessage('Please enter the OTP we sent to your email.')
        else if (otpError) setSnackBarMessage(otpError.message)
    }, [loginError?.message, otpError?.message])

    return (
        <Layout
            isLoading={requestOtpLoading || loginLoading}
            maxWidth="sm"
            showHeader
            title="Login"
        >
            {step === 'email' ? (
                <form onSubmit={onRequestOtp}>
                    <Stack gap="1.5rem">
                        <TextField
                            label="Email"
                            variant="outlined"
                            value={email}
                            onChange={(e) => onEmailChange(e.target.value)}
                        />

                        <Button
                            disabled={!isEmailValid}
                            size="large"
                            type="submit"
                            variant="contained"
                        >
                            Send OTP
                        </Button>
                    </Stack>
                </form>
            ) : null}

            {step === 'otp' ? (
                <form onSubmit={onLogin}>
                    <Stack gap="1.5rem">
                        <Typography>
                            Please check your email for a one-time passcode (OTP) that you can use to log in.
                        </Typography>

                        <TextField
                            label="One Time Passcode (OTP)"
                            variant="outlined"
                            value={otp}
                            onChange={(e) => onOtpChange(e.target.value)}
                        />

                        <Stack
                            direction="row"
                            gap="1.5rem"
                            useFlexGap
                        >
                            <Button
                                disabled={timeLeft !== 0}
                                fullWidth
                                onClick={onResend}
                                size="large"
                                variant="outlined"
                            >
                                {timeLeft === 0 ? 'Resend' : `Resend in ${(timeLeft / 1000)}`}
                            </Button>

                            <Button
                                disabled={!isOtpValid}
                                fullWidth
                                size="large"
                                type="submit"
                                variant="contained"
                            >
                                Login
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            ) : null}

            <ErrorMessage
                handleClose={() => setSnackBarMessage('')}
                isOpen={snackBarMessage !== ''}
                message={snackBarMessage}
            />
        </Layout>
    )
}
